import { IProfileAddressModel } from '@/interfaces/profile';
import { IStoreAddress, IStoreLists } from '@/interfaces/store';
import { ILocation } from '@/interfaces/location';
import { BANGLADESH_BOUNDS } from '@/constants';

export const getLocationData = (dataList?: any[]) => {
  const options: { label: string; value: string }[] = [];
  dataList?.forEach((data) => {
    options.push({
      label: data?.Name,
      value: data?.Id,
    });
  });
  return options;
};

export const getStoreData = (storeList?: IStoreLists[]) => {
  const storeLocations: IStoreAddress[] = [];
  storeList?.forEach((data) => {
    storeLocations.push({
      id: data?.Id,
      name: data.Name,
      address: data?.Address,
      lat: data?.Latitude,
      lon: data?.Longitude,
      phone: data?.PhoneNumber,
      weekday: data?.OpeningDays,
      time: data?.OpeningHours,
    });
  });
  return storeLocations;
};

export const getAddressData = (addressType: string, addressData?: IProfileAddressModel[]) => {
  let filteredAddress: IProfileAddressModel[] = [];
  addressData?.forEach((address: IProfileAddressModel) => {
    if (address?.AddressType === addressType) {
      filteredAddress.push(address);
    }
  });
  return filteredAddress;
};

export const haversineDistanceKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  if (!Number.isFinite(lat1) || !Number.isFinite(lon1) || !Number.isFinite(lat2) || !Number.isFinite(lon2)) {
    return NaN;
  }
  if (lat1 === lat2 && lon1 === lon2) {
    return 0;
  }
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const resolveNearestThana = <
  T extends { Id: string; Name: string; Lat: string | number; Long: string | number },
>(
  lat: number,
  lng: number,
  thanas?: T[] | null,
): T | undefined => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !thanas || thanas.length === 0) {
    return undefined;
  }

  let nearest: T | undefined;
  let minDistance = Infinity;

  for (const thana of thanas) {
    if (thana.Lat === '' || thana.Long === '') {
      continue;
    }
    const tLat = parseFloat(thana.Lat as string);
    const tLng = parseFloat(thana.Long as string);
    if (Number.isNaN(tLat) || Number.isNaN(tLng)) {
      continue;
    }

    const distance = haversineDistanceKm(lat, lng, tLat, tLng);
    if (Number.isNaN(distance)) {
      continue;
    }

    if (distance < minDistance) {
      minDistance = distance;
      nearest = thana;
    }
  }

  return nearest;
};

/** Resolve a thana by id: district-scoped `thanaData` first, then bulk
 *  `allLocationList.Thanas`. Keeps the Confirm handler in sync with the
 *  dropdown source-of-truth. */
export const resolveThanaById = (
  id: string,
  thanaData?: ILocation[] | null,
  allLocationList?: { Thanas?: ILocation[] | null },
): ILocation | undefined =>
  thanaData?.find((t) => t.Id === id) ?? allLocationList?.Thanas?.find((t) => t.Id === id);

/** True when `(lat, lng)` lies inside the service-area bbox.
 *  Defaults to Bangladesh; non-finite coordinates return false. */
export const isWithinServiceArea = (
  lat: number,
  lng: number,
  bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number } = BANGLADESH_BOUNDS,
): boolean => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return false;
  }
  return lat >= bounds.minLat && lat <= bounds.maxLat && lng >= bounds.minLng && lng <= bounds.maxLng;
};
